import apiService from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const callSettingsService = {
  /**
   * Get call settings for the logged-in user's tenant
   * Returns provider type and available phone numbers for making calls
   */
  getCallSettings: async () => {
    try {
      const response = await apiService.get(`${API_BASE_URL}/settings/call-settings`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call settings:', error);
      throw error;
    }
  },

  /**
   * Get available phone numbers for the current provider
   * Filters and returns phone numbers based on the selected provider
   */
  getAvailablePhoneNumbers: async () => {
    try {
      const response = await callSettingsService.getCallSettings();
      
      if (!response.success || !response.data) {
        return [];
      }

      const { provider, plivoSettings, twilioSettings } = response.data;
      const settings = provider === 'plivo' ? plivoSettings : twilioSettings;
      
      if (!settings || !settings.countries || settings.countries.length === 0) {
        return [];
      }

      // Flatten all phone numbers from all countries
      const phoneNumbers = [];
      settings.countries.forEach(country => {
        if (country.phoneNumbers && Array.isArray(country.phoneNumbers)) {
          phoneNumbers.push(...country.phoneNumbers);
        }
      });

      return phoneNumbers;
    } catch (error) {
      console.error('Error getting available phone numbers:', error);
      return [];
    }
  },

  /**
   * Get Plivo credentials for making calls
   */
  getPlivoCredentials: async () => {
    try {
      const response = await callSettingsService.getCallSettings();
      
      if (!response.success || !response.data) {
        return null;
      }

      const { plivoSettings } = response.data;
      
      if (!plivoSettings || !plivoSettings.countries || plivoSettings.countries.length === 0) {
        return null;
      }

      // Return the first country's Plivo credentials
      const firstCountry = plivoSettings.countries[0];
      return {
        authId: firstCountry.authId,
        authToken: firstCountry.authToken,
        sipUser: firstCountry.sipUser,
        sipDomain: firstCountry.sipDomain,
        sipPassword: firstCountry.sipPassword,
        phoneNumbers: firstCountry.phoneNumbers || []
      };
    } catch (error) {
      console.error('Error getting Plivo credentials:', error);
      return null;
    }
  },

  /**
   * Get Twilio credentials for making calls
   */
  getTwilioCredentials: async () => {
    try {
      const response = await callSettingsService.getCallSettings();
      
      if (!response.success || !response.data) {
        return null;
      }

      const { twilioSettings } = response.data;
      
      if (!twilioSettings || !twilioSettings.countries || twilioSettings.countries.length === 0) {
        return null;
      }

      // Return the first country's Twilio credentials
      const firstCountry = twilioSettings.countries[0];
      return {
        accountSid: firstCountry.accountSid,
        authToken: firstCountry.authToken,
        fromNumber: firstCountry.fromNumber,
        phoneNumbers: firstCountry.phoneNumbers || []
      };
    } catch (error) {
      console.error('Error getting Twilio credentials:', error);
      return null;
    }
  },

  /**
   * Get provider type (plivo or twilio)
   */
  getProvider: async () => {
    try {
      const response = await callSettingsService.getCallSettings();
      
      if (!response.success || !response.data) {
        return 'plivo'; // Default provider
      }

      return response.data.provider || 'plivo';
    } catch (error) {
      console.error('Error getting provider:', error);
      return 'plivo'; // Default provider
    }
  }
};

export default callSettingsService;
